<template>
    <div class="modal fade show hotel-modal" aria-labelledby="refundModalTitle">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div class="modal-content position-relative">
                <div v-if="pendingAction" class="confirm-overlay">
                    <div class="confirm-box">
                        <div class="confirm-icon" :class="confirmIconClass">
                            <i class="fa fa-exclamation-triangle"></i>
                        </div>
                        <div class="confirm-title">{{ confirmTitle }}</div>
                        <div class="confirm-text">{{ confirmText }}</div>
                        <div class="d-flex justify-content-center gap-1 mt-2">
                            <button type="button" class="btn btn-outline-secondary" @click="pendingAction = ''">No, Go Back</button>
                            <button type="button" :disabled="loading" class="btn" :class="confirmButtonClass" @click="onConfirmAction">
                                {{ confirmButtonLabel }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-1 px-1 my-1">
                    <h1 class="modal-heading mb-0">Cancel Booking</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <div class="modal-body">
                    <div v-if="detailLoading" class="text-center py-3">Loading reservation…</div>

                    <div class="alert alert-warning py-1 px-2 mb-2">
                        Both actions cancel this booking and release its rooms. Refund returns the advance to the guest; cancel without refund keeps the advance on the guest account. This cannot be undone.
                    </div>

                    <div class="detail-section mb-2">
                        <div class="section-title">Reservation Details</div>
                        <div class="row g-1">
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Reservation No</div>
                                <div class="detail-value">{{ textValue(details.reservation_no) }}</div>
                            </div>
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Guest</div>
                                <div class="detail-value">{{ textValue(details.guest_name) }}</div>
                            </div>
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Mobile No</div>
                                <div class="detail-value">{{ textValue(details.guest_mobile) }}</div>
                            </div>
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Check-in</div>
                                <div class="detail-value">{{ textValue(details.check_in_formatted) }}</div>
                            </div>
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Check-out</div>
                                <div class="detail-value">{{ textValue(details.check_out_formatted) }}</div>
                            </div>
                            <div class="col-md-4 col-6">
                                <div class="detail-label">Rooms</div>
                                <div class="detail-value">{{ roomNames }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="row g-1 mb-2">
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-total">
                                <span class="summary-label">Estimate Bill</span>
                                <span class="summary-value">{{ commaFormat(details.total) }}</span>
                            </div>
                        </div>
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-paid">
                                <span class="summary-label">Paid</span>
                                <span class="summary-value">{{ commaFormat(details.paid) }}</span>
                            </div>
                        </div>
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-refund">
                                <span class="summary-label">Refundable</span>
                                <span class="summary-value">{{ commaFormat(details.refundable) }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="paymentRows.length" class="detail-section mb-2">
                        <div class="section-title">Payment History</div>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered detail-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Voucher</th>
                                        <th>Account</th>
                                        <th class="text-end">Paid</th>
                                        <th class="text-end">Refunded</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="payment in paymentRows" :key="payment.id">
                                        <td>{{ textValue(payment.receipt_date) }}</td>
                                        <td>{{ textValue(payment.voucher_no) }}</td>
                                        <td>{{ textValue(payment.account_head_name) }}</td>
                                        <td class="text-end">{{ commaFormat(payment.total_paid_amount) }}</td>
                                        <td class="text-end">{{ commaFormat(payment.refund_amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div v-if="hasRefundable" class="section-title">Refund Info</div>

                    <form @submit.prevent="onSubmit">
                        <div v-if="hasRefundable" class="row g-1">
                            <div class="col-md-6 mb-1">
                                <label class="form-label">Refund From</label>
                                <v-select
                                    placeholder="Select Cash & Bank Account"
                                    v-model="formData.account_head_id"
                                    :options="accountHeads"
                                    :reduce="head => head.id"
                                    label="name"
                                />
                            </div>
                            <div class="col-md-6 mb-1">
                                <label class="form-label">Refund Date</label>
                                <input v-model="formData.date" type="date" class="form-control"/>
                            </div>
                            <div class="col-md-6 mb-1">
                                <label class="form-label">Refund Amount</label>
                                <input v-model="formData.amount" type="number" min="0" :max="details.refundable" class="form-control"/>
                            </div>
                            <div class="col-md-6 mb-1">
                                <label class="form-label">Reference</label>
                                <input v-model="formData.reference" type="text" class="form-control"/>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap justify-content-end gap-1 mt-2">
                            <button type="button" class="btn btn-outline-secondary flex-grow-1 flex-sm-grow-0" @click="closeModal">Close</button>
                            <button
                                type="button"
                                :disabled="loading"
                                class="btn btn-success flex-grow-1 flex-sm-grow-0"
                                @click="onClickCheckin"
                            >
                                Check-In
                            </button>
                            <button
                                type="button"
                                :disabled="loading"
                                class="btn btn-warning flex-grow-1 flex-sm-grow-0"
                                @click="onClickCancelWithoutRefund"
                            >
                                Cancel Without Refund
                            </button>
                            <button v-if="hasRefundable" type="submit" :disabled="loading" class="btn btn-danger flex-grow-1 flex-sm-grow-0">Refund &amp; Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';
import handleReceipt from '@/services/modules/receipt';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    reservation: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['onSubmit', 'onClose', 'onCheckin']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { refundHotelReservation, cancelHotelReservation, fetchSingleHotelReservation } = handleHotel();
const { fetchCashAndBank } = handleReceipt();
const { commaFormat } = figureFormatter();

const loading = ref(false);
const detailLoading = ref(false);
const details = ref({});
const accountHeads = ref([]);
const pendingAction = ref('');
const formData = ref({
    company_id: $route.params.companyId,
    account_head_id: null,
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    reference: '',
});

const textValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const roomNames = computed(() => {
    if (! details.value.rooms) return '-';
    return details.value.rooms.map(room => room.name).join(', ');
});

const paymentRows = computed(() => {
    if (! details.value.payments) return [];
    return details.value.payments;
});

const hasRefundable = computed(() => Number(details.value.refundable) > 0);

const isCancelAction = computed(() => pendingAction.value === 'cancel');

const confirmTitle = computed(() => {
    if (isCancelAction.value) return 'Cancel Without Refund?';
    return 'Refund & Cancel?';
});

const confirmText = computed(() => {
    if (isCancelAction.value) {
        return 'This booking will be cancelled and its rooms released. The unused advance of ' + commaFormat(details.value.refundable) + ' will stay on the guest account.';
    }
    return commaFormat(formData.value.amount) + ' will be refunded to the guest, then this booking will be cancelled and its rooms released.';
});

const confirmButtonLabel = computed(() => {
    if (isCancelAction.value) return 'Yes, Cancel Booking';
    return 'Yes, Refund & Cancel';
});

const confirmButtonClass = computed(() => {
    if (isCancelAction.value) return 'btn-warning';
    return 'btn-danger';
});

const confirmIconClass = computed(() => {
    if (isCancelAction.value) return 'icon-warning';
    return 'icon-danger';
});

const closeModal = () => {
    emit('onClose');
};

const validate = () => {
    if (! formData.value.account_head_id) {
        showError('Please select the refund account');
        return false;
    }
    if (! formData.value.amount || formData.value.amount <= 0) {
        showError('Please enter a valid refund amount');
        return false;
    }
    if (Number(formData.value.amount) > Number(details.value.refundable)) {
        showError('Refund amount cannot exceed the refundable advance');
        return false;
    }
    return true;
};

const getReservationDetails = async () => {
    detailLoading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    await fetchSingleHotelReservation(query, props.reservation.id).then(res => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        details.value = res.data;
        formData.value.amount = res.data.refundable;
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        detailLoading.value = false;
    });
};

const handleActionResponse = (res) => {
    if (! res.status) {
        showError(res.message);
        return;
    }
    showSuccess(res.message);
    emit('onSubmit');
    closeModal();
};

const onClickCheckin = () => {
    emit('onCheckin', props.reservation);
};

const onClickCancelWithoutRefund = () => {
    pendingAction.value = 'cancel';
};

const onSubmit = () => {
    if (! validate()) return;
    pendingAction.value = 'refund';
};

const runCancelWithoutRefund = async () => {
    const payload = { company_id: $route.params.companyId };
    await cancelHotelReservation(payload, props.reservation.id).then(res => {
        handleActionResponse(res);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
        pendingAction.value = '';
    });
};

const runRefundAndCancel = async () => {
    await refundHotelReservation(formData.value, props.reservation.id).then(res => {
        handleActionResponse(res);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
        pendingAction.value = '';
    });
};

const onConfirmAction = async () => {
    loading.value = true;
    if (pendingAction.value === 'cancel') return runCancelWithoutRefund();
    return runRefundAndCancel();
};

onMounted(() => {
    getReservationDetails();
    fetchCashAndBank($route.params.companyId).then(res => {
        if (res.data) accountHeads.value = res.data;
    });
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.section-title {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.5rem;
}
.detail-section {
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.75rem;
}
.detail-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6e6b7b;
    opacity: 0.85;
}
.detail-value {
    font-size: 0.88rem;
    font-weight: 600;
    color: #384250;
    word-break: break-word;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.65rem 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    opacity: 0.75;
    margin-bottom: 0.15rem;
}
.summary-value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-refund {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
.confirm-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: #ffffffe6;
    border-radius: 0.35rem;
}
.confirm-box {
    max-width: 26rem;
    text-align: center;
    background: #ffffff;
    border: 1px solid #ebe9f1;
    border-radius: 0.6rem;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16);
}
.confirm-icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 0.65rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 1.3rem;
}
.confirm-icon.icon-danger {
    color: #c0392b;
    background: #fdeceb;
}
.confirm-icon.icon-warning {
    color: #b7791f;
    background: #fdf3e2;
}
.confirm-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #384250;
    margin-bottom: 0.35rem;
}
.confirm-text {
    font-size: 0.85rem;
    color: #6e6b7b;
    line-height: 1.45;
}
.modal-heading {
    font-size: 1.5rem;
}
.detail-table {
    min-width: 480px;
}
@media (max-width: 767.98px) {
    .modal-heading {
        font-size: 1.15rem;
    }
    .detail-section {
        padding: 0.6rem;
    }
    .summary-card {
        flex-direction: row;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
    }
    .summary-label {
        margin-bottom: 0;
    }
    .summary-value {
        font-size: 0.95rem;
    }
}
</style>
